import cx from "classnames";

import { Box, Flex } from "metabase/ui";
import type { Database as ApiDatabase } from "metabase-types/api";

import { useWorkbenchState } from "../context";

import S from "./AdhocWorkbench.module.css";
import { QueryTabPanel } from "./QueryTabPanel";
import { WorkbenchTabs } from "./WorkbenchTabs";

export type AdhocWorkbenchProps = {
  databases: ApiDatabase[];
};

/**
 * The workbench shell: a tab strip over a stack of tab panels.
 *
 * Panels are mounted lazily — a tab is only rendered once it has been active at
 * least once — and thereafter stay mounted, hidden with `display: none`.
 *
 * Both halves of that matter:
 *
 * - Lazy mount, because `DebouncedFrame` only renders its children after it has
 *   measured a non-zero width. A panel that first mounted hidden would never
 *   show its visualization at all.
 * - Stay mounted with `display: none` (rather than `visibility: hidden` or
 *   unmounting), because `ExplicitSize` keeps its last measurement when an
 *   element has no box, so switching back is instant, and because the result
 *   tables keep their scroll position and column widths.
 */
export function AdhocWorkbench({ databases }: AdhocWorkbenchProps) {
  const { tabs, activeTabId, runtimeByTabId, visitedTabIds } =
    useWorkbenchState();

  return (
    <Flex className={S.workbench} direction="column" h="100%" w="100%">
      <WorkbenchTabs tabs={tabs} activeTabId={activeTabId} />
      <Box className={S.panels}>
        {tabs.map((tab) => {
          const runtime = runtimeByTabId[tab.id];
          if (!visitedTabIds.includes(tab.id) || !runtime) {
            return null;
          }
          const isActive = tab.id === activeTabId;
          return (
            <Box
              key={tab.id}
              className={cx(S.panel, { [S.hidden]: !isActive })}
              data-testid="adhoc-query-tab-panel"
              data-tab-id={tab.id}
              data-active={isActive}
            >
              <QueryTabPanel
                tab={tab}
                runtime={runtime}
                isActive={isActive}
                databases={databases}
              />
            </Box>
          );
        })}
      </Box>
    </Flex>
  );
}
